/**
 * Builds the `filter` body for the collection hexagons endpoint
 * (POST /collections/{uuid}/hexagons).
 *
 * ## Why this exists — count agreement
 *
 * The collection view counts the same detections twice, via two independent
 * paths that must agree or the numbers drift:
 *   - the detection list/tree + map shapes are filtered client-side in JS
 *     (`filterFeatures`);
 *   - the hexagon cells are aggregated server-side in SQL from this body.
 *
 * Two mismatches have to be reconciled here for the paths to agree:
 *
 * 1. **Level folding.** The UI has a collection-level filter (applied to every
 *    object) and per-object filters. The endpoint only accepts a per-object map,
 *    so the collection-level range is folded into each object's range as an
 *    intersection — a detection passes only if it satisfies *both*, i.e. the
 *    tighter lower bound and the tighter upper bound win.
 *
 * 2. **Visibility as a selector.** The endpoint treats the object keys (node uuids) present
 *    in the map as the set to aggregate (an empty/omitted map aggregates
 *    *everything*). So visibility is expressed by *which keys are emitted*: only
 *    visible (checked) objects are included; hidden ones are omitted. A visible
 *    object with no active range is still emitted with an empty `{}` so it stays
 *    aggregated.
 *
 * ## Source of values
 *
 * Values come from the *persisted* collection config (raw API units — confidence
 * 0–1, height/area/radius in metric base units), not the live slider draft. The
 * hexagon query therefore refetches when filters are applied (config changes),
 * not on every slider tick — matching the "update on apply" behavior.
 *
 * ## Caller contract
 *
 * When no objects are visible this returns `{}`, which the endpoint would read
 * as "aggregate everything". The caller (the hexagon query hook) must therefore
 * gate the request on there being at least one visible object, rather than
 * relying on an empty body to mean "show nothing".
 *
 * @module buildHexagonFilterBody
 */

/**
 * Intersect a collection-level range and an object-level range for one
 * parameter. `null` bounds are open-ended (-∞ / +∞), so the intersection keeps
 * the tighter defined bound on each side and stays `null` when neither level
 * constrains that side.
 *
 * @param {{min?: number|null, max?: number|null}} [collectionRange]
 * @param {{min?: number|null, max?: number|null}} [objectRange]
 * @returns {{min: number|null, max: number|null}}
 */
const intersectRange = (collectionRange, objectRange) => {
	const mins = [collectionRange?.min, objectRange?.min].filter(v => v != null);
	const maxs = [collectionRange?.max, objectRange?.max].filter(v => v != null);
	return {
		min: mins.length ? Math.max(...mins) : null,
		max: maxs.length ? Math.min(...maxs) : null,
	};
};

/**
 * Build the per-object filter map for the hexagons endpoint.
 *
 * @param {object} savedCollectionFilters - persisted collection-level filters,
 *   `{ [parameterId]: { min, max } }` in raw API units. `{}` when none saved.
 * @param {Array<{key: string, savedFilters?: object}>} objects - collection
 *   objects; each `savedFilters` is `{ [parameterId]: { min, max } }` (raw).
 * @param {Record<string, boolean>} visibleKeys - `{ [nodeUuid]: true }` for
 *   currently visible objects (the store's `checkedNodes`).
 * @returns {Record<string, Record<string, {min: number|null, max: number|null}>>}
 *   `{ [nodeUuid]: { [parameterId]: { min, max } } }` for visible objects only.
 */
export const buildHexagonFilterBody = (
	savedCollectionFilters = {},
	objects = [],
	visibleKeys = {}
) => {
	const body = {};

	for (const obj of objects) {
		// Hidden objects are excluded from aggregation by omitting their key.
		if (!visibleKeys[obj.key]) continue;

		const objectFilters = obj.savedFilters ?? {};
		const parameterIds = new Set([
			...Object.keys(savedCollectionFilters),
			...Object.keys(objectFilters),
		]);

		const ranges = {};
		// A disjoint intersection (e.g. collection min=5, object max=3) means no
		// detection can satisfy both levels for this parameter, so the object as a
		// whole can never match — exclude it below rather than sending an inverted
		// {min, max} whose backend interpretation isn't guaranteed, mirroring how
		// computeEffectiveFilter (src/components/propertyFilters) resolves the same
		// collection/object collapse client-side instead of relying on the query layer.
		let isImpossible = false;
		for (const id of parameterIds) {
			const { min, max } = intersectRange(savedCollectionFilters[id], objectFilters[id]);
			if (min != null && max != null && min > max) {
				isImpossible = true;
				break;
			}
			// Drop fully-open predicates — they impose no restriction, and omitting
			// them keeps the body minimal and readable.
			if (min != null || max != null) ranges[id] = { min, max };
		}
		if (isImpossible) continue;

		// Present key = aggregated. Empty ranges mean "this object, no predicates".
		body[obj.key] = ranges;
	}

	return body;
};

export default buildHexagonFilterBody;
